// Character / Profile page — v2 redesign matching the lobby reference style.
// Two-column dashboard: left = identity + character preview + progress,
// right = customisation tabs (Hat / Pattern / Shirt / Pants / Skin).
// The right side reuses <AvatarEditor /> but its built-in 3D stage is hidden
// via CSS so the preview lives in the left card only.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { AvatarEditor } from "../components/AvatarEditor";
import { AvatarViewer3D } from "../components/AvatarViewer3D";
import { updateAvatar, updateName } from "../local/profile";

export function LocalCharacter() {
  const { profile, setProfile } = useLocalProfile();
  const nav = useNavigate();
  const [name, setName] = useState(profile?.displayName ?? "");
  const [editingName, setEditingName] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Draft avatar — drives the live 3D preview as the user clicks upgrades.
  // Persisted to profile when AvatarEditor's "Save" fires onSaved.
  const [draftAvatar, setDraftAvatar] = useState(profile?.avatarConfig);

  if (!profile) {
    nav("/", { replace: true });
    return null;
  }

  const saveName = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === profile.displayName) { setEditingName(false); return; }
    setProfile(updateName(profile, trimmed));
    setSavedAt(Date.now());
    setEditingName(false);
  };
  const saveAvatar = (cfg: Parameters<typeof updateAvatar>[1]) => {
    setProfile(updateAvatar(profile, cfg));
    setDraftAvatar(cfg);
    setSavedAt(Date.now());
  };

  return (
    <div className="profile-v2-page">
      {/* Top bar — just back + title + a single coins chip (decluttered). */}
      <header className="profile-v2-top">
        <button className="profile-v2-back" onClick={() => nav("/")} aria-label="Back">←</button>
        <div className="profile-v2-titleblock">
          <h1>My Character</h1>
          <p>Pick a hat, colour and pattern</p>
        </div>
        <div className="profile-v2-hud">
          <div className="profile-v2-hudcard">
            <span className="profile-v2-hudicon profile-v2-coin" aria-hidden>●</span>
            <span className="profile-v2-hudmeta">
              <strong>{profile.coins}</strong>
              <small>Coins</small>
            </span>
          </div>
        </div>
        {savedAt && <span className="profile-v2-saved">Saved {new Date(savedAt).toLocaleTimeString()}</span>}
      </header>

      <div className="profile-v2-grid">
        {/* LEFT column — name + player code (top) + STATIC character
         *   preview (bottom). Progress card removed per playtest. */}
        <aside className="profile-v2-left">
          {profile.code && (
            <div className="profile-v2-card profile-v2-codecard">
              <small>Your player code</small>
              <div className="profile-v2-code">{profile.code}</div>
              <small className="t-muted">Use this to log in from another device.</small>
            </div>
          )}

          <div className="profile-v2-card profile-v2-namecard">
            <div className="profile-v2-namerow">
              <div className="profile-v2-namemeta">
                <small>Name</small>
                {editingName ? (
                  <input
                    className="profile-v2-nameinput"
                    value={name}
                    autoFocus
                    onChange={(e) => setName(e.target.value)}
                    onBlur={saveName}
                    onKeyDown={(e) => { if (e.key === "Enter") saveName(); }}
                    maxLength={20}
                  />
                ) : (
                  <strong>{profile.displayName}</strong>
                )}
              </div>
              <button
                className="profile-v2-edit"
                onClick={() => setEditingName((v) => !v)}
                aria-label="Edit name"
              >✎<span>Edit</span></button>
            </div>
          </div>

          <div className="profile-v2-card profile-v2-previewcard profile-v2-previewcard-sticky">
            <div className="profile-v2-prevhead">
              <strong>Character Preview</strong>
              <span className="profile-v2-info" aria-hidden>ⓘ</span>
            </div>
            <div className="profile-v2-prevstage">
              <AvatarViewer3D avatar={draftAvatar ?? profile.avatarConfig} />
            </div>
            <button
              className="profile-v2-savebtn"
              onClick={() => {
                if (draftAvatar) saveAvatar(draftAvatar);
                nav("/");
              }}
            >
              <span aria-hidden>💾</span> Save & Return
            </button>
          </div>
        </aside>

        {/* RIGHT column — customisation */}
        <section className="profile-v2-card profile-v2-right">
          <div className="profile-v2-rightinner">
            <AvatarEditor
              initial={profile.avatarConfig}
              onChange={setDraftAvatar}
              onSaved={saveAvatar}
              localOnly
              unlocked={profile.unlocked}
              lifetimeRuns={profile.lifetimeRuns}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
